import {
  addDays,
  dueTone,
  weekdayOf,
  type CalendarAllDayEvent,
  type CalendarBlock,
  type CalendarDueItem,
  type ISODate,
} from "@app/shared";

import { BlockChip, GhostSlot, GridColumn, NowLine, TimeGutter, useGridScroll } from "./grid";
import { DayHeaderCell } from "./sidebar";
import { PALETTE, blockMotion } from "./palette";
import { anchorOfElement, type PeekAnchor } from "../../lib/popover";
import type { BlockDrag, BlockDragMode } from "./useBlockDrag";

// ============================================================================
// The week grid — docs/design/handoff/"LifeOS Calendar.dc.html" option 11a.
//
// Seven columns, a 52px gutter, and an ALL-DAY ROW that carries due dates rather than banners
// (brief → Time grid). The distinction is not cosmetic: a deadline has an instant and no
// duration, so it can never be placed on the grid without the screen inventing a length for it.
// ============================================================================

/** The gutter is 52px in week view — the brief's number. */
const GUTTER = 52;

/**
 * ONE TRACK LIST FOR ALL THREE ROWS, and `minmax(0, 1fr)` rather than `1fr`.
 *
 * The header, the due row and the grid body have to agree about where every column starts, and
 * flex cannot promise that: a flex item's automatic minimum is its CONTENT's, so one long real
 * Canvas title ("Reading response 2: The Extended Mind") widens its column, then the whole card
 * past the viewport, and the week silently becomes six days with the seventh off-screen. A grid
 * whose tracks are explicitly allowed to shrink to zero cannot do that, whatever is in them.
 */
const COLUMNS = "grid grid-cols-[52px_repeat(7,minmax(0,1fr))]";

export function WeekView({
  weekStart,
  today,
  nowMinutes,
  blocks,
  dueItems,
  allDayEvents = [],
  drag,
  onOpenDay,
  onOpenBlock,
  onOpenDue,
  onOpenAllDay,
  onCompose,
  onDragBegin,
}: {
  weekStart: ISODate;
  today: ISODate | undefined;
  nowMinutes: number | null;
  blocks: CalendarBlock[];
  dueItems: CalendarDueItem[];
  /** Google's whole-day events. They belong in the all-day row and NOWHERE on the grid. */
  allDayEvents?: CalendarAllDayEvent[];
  /**
   * A block being moved or resized, or null. In WEEK view a move is two-dimensional: the ghost
   * follows the pointer into whichever column it is over, so `drag.date` is the day it would
   * land on and not necessarily the day it came from.
   */
  drag: BlockDrag | null;
  onOpenDay: (date: ISODate) => void;
  onOpenBlock: (block: CalendarBlock, anchor: PeekAnchor) => void;
  /** The all-day row's due pills open the detail peek — Canvas's own deadline, no edit fields. */
  onOpenDue: (item: CalendarDueItem, anchor: PeekAnchor) => void;
  /** Same row, Google's whole-day events. */
  onOpenAllDay: (event: CalendarAllDayEvent, anchor: PeekAnchor) => void;
  /** The day, the minute, and the point the click landed at — the editor opens beside it. */
  onCompose: (date: ISODate, minutes: number, anchor: PeekAnchor) => void;
  onDragBegin: (
    block: CalendarBlock,
    mode: BlockDragMode,
    event: React.PointerEvent<HTMLElement>,
  ) => void;
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const body = useGridScroll();
  const isWeekend = (date: ISODate) =>
    weekdayOf(date) === 0 || weekdayOf(date) === 6;

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-panel border border-line-2 bg-surface shadow-calendar">
      {/* Column headers. Clicking one opens that day — the brief's week ↔ day move. */}
      <div className={`${COLUMNS} flex-none border-b border-line-4`}>
        <div className="box-border border-r border-cal-column" />
        {days.map((date) => (
          <DayHeaderCell
            key={date}
            date={date}
            today={today}
            onOpen={onOpenDay}
          />
        ))}
      </div>

      {/* THE ALL-DAY ROW. Due dates, not banners — plus Google's whole-day events, which are the
          one other thing on this screen that has a DAY and no time of day. They go here for the
          same reason the deadlines do: putting one on the 7 AM–10 PM grid would mean the screen
          choosing an hour Google never gave. The gutter label says DUE / ALL-DAY once there is
          something all-day in the week, and stays DUE when there is not. */}
      <div
        className={`${COLUMNS} min-h-[46px] flex-none border-b border-line-4 bg-cal-weekend`}
      >
        <div className="box-border border-r border-cal-column px-2 pt-2 text-right text-[8.5px] font-semibold leading-[1.2] tracking-[.05em] text-faintest-2">
          {allDayEvents.length > 0 ? "DUE / ALL-DAY" : "DUE"}
        </div>
        {days.map((date) => {
          const items = dueItems.filter((d) => d.date === date);
          return (
            <div
              key={date}
              className={[
                // See DayHeaderCell for why `min-w-0` is here: real Canvas titles are long, and a
                // flex column without it takes its minimum from the longest one.
                "box-border flex min-w-0 flex-1 flex-col gap-1 border-r border-cal-column px-[5px] py-[7px] last:border-r-0",
                isWeekend(date) ? "bg-cal-weekend-head" : "",
              ].join(" ")}
            >
              {items.map((item) => {
                const palette =
                  PALETTE[today ? dueTone(item, today) : "canvas"];
                return (
                  <button
                    key={item.id}
                    type="button"
                    // READ-ONLY, LIKE EVERYTHING CANVAS OWNS — the click opens a peek, never an
                    // editor. That is a fact about what happens on click, not about the tag: a
                    // `<button>` here is the honest control for "opens something", same as the
                    // day view's due pills right above it in the same file tree.
                    onClick={(e) => onOpenDue(item, anchorOfElement(e.currentTarget))}
                    // 12d, and `rounded-keycap` (6px) is what picks the mechanic: a radius ≤ 10
                    // surface takes `.mo-rule`, so the deadline's own tone arrives as a straight
                    // left rule rather than as a lift. Press is the app-wide 1px.
                    style={blockMotion(palette)}
                    className={[
                      "mo-rule mo-press flex items-center gap-[6px] rounded-keycap border-l-[2.5px] px-[7px] py-1 text-left",
                      palette.tint,
                      palette.rule,
                    ].join(" ")}
                    title={
                      item.courseCode
                        ? `${item.courseCode} · from Canvas`
                        : "From Canvas"
                    }
                  >
                    <span
                      className={[
                        "min-w-0 flex-1 truncate text-[10.5px] font-semibold leading-[1.3]",
                        palette.ink,
                      ].join(" ")}
                    >
                      {item.label}
                    </span>
                    <span
                      className={[
                        "flex-none text-[8px] font-semibold leading-none tracking-[.04em]",
                        palette.ink2,
                      ].join(" ")}
                    >
                      CANVAS
                    </span>
                  </button>
                );
              })}

              {/* Google's whole-day events. A multi-day event appears on every day it covers —
                  `date` … `endDate` inclusive — because a three-day trip is on the calendar all
                  three days, not only on the first. Read-only, so a div: there is no row behind
                  it that any endpoint here would accept a write for. */}
              {allDayEvents
                .filter((e) => e.date <= date && date <= e.endDate)
                .map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={(e) => onOpenAllDay(event, anchorOfElement(e.currentTarget))}
                    style={blockMotion(PALETTE.google)}
                    className={[
                      "mo-rule mo-press flex items-center gap-[6px] rounded-keycap border-l-[2.5px] px-[7px] py-1 text-left",
                      PALETTE.google.tint,
                      PALETTE.google.rule,
                    ].join(" ")}
                    title={[
                      "From Google Calendar",
                      event.location ?? undefined,
                      // Google's own word, never softened into "confirmed" by omission.
                      event.status === "tentative" ? "marked tentative in Google" : undefined,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  >
                    <span
                      className={[
                        "min-w-0 flex-1 truncate text-[10.5px] font-semibold leading-[1.3]",
                        PALETTE.google.ink,
                        event.status === "tentative" ? "italic" : "",
                      ].join(" ")}
                    >
                      {event.title}
                    </span>
                    <span
                      className={[
                        "flex-none text-[8px] font-semibold leading-none tracking-[.04em]",
                        PALETTE.google.ink2,
                      ].join(" ")}
                    >
                      GOOGLE
                    </span>
                  </button>
                ))}
            </div>
          );
        })}
      </div>

      {/* The grid body. The gutter scrolls WITH the columns — it is a track in the same grid —
          or the labels drift off the rules. Twenty-four hours tall since the window stopped
          pretending the day starts at 7; it opens on the morning (see `useGridScroll`). */}
      <div ref={body} className="min-h-0 flex-1 overflow-y-auto">
        <div className={COLUMNS}>
          <TimeGutter
            width={GUTTER}
            nowMinutes={today && days.includes(today) ? nowMinutes : null}
          />
          {days.map((date) => (
            <GridColumn
              key={date}
              date={date}
              today={date === today}
              weekend={isWeekend(date)}
              onPick={(minutes, at) => onCompose(date, minutes, at)}
              className="box-border border-r border-cal-column last:border-r-0"
            >
              {blocks
                .filter((b) => b.date === date)
                .map((block) => (
                  <BlockChip
                    key={block.id}
                    block={block}
                    size="week"
                    onOpen={onOpenBlock}
                    onDragBegin={onDragBegin}
                    dragging={drag?.moved === true && drag.block.id === block.id}
                  />
                ))}
              {/* The proposal, drawn in whichever column the pointer is over — which is how a
                  block moves to another day: the ghost is already there before the drop is. */}
              {drag?.moved && drag.date === date && (
                <GhostSlot
                  label={drag.block.title}
                  startMinutes={drag.startMinutes}
                  endMinutes={drag.endMinutes}
                  size="week"
                />
              )}
              {date === today && nowMinutes !== null && (
                <NowLine minutes={nowMinutes} />
              )}
            </GridColumn>
          ))}
        </div>
      </div>
    </div>
  );
}
